import { lessonTypes } from "@/constants/homework";
import { format } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";

export function formatScore(score) {
  if (isNaN(score)) {
    return "--";
  } else if (score === 0) {
    return "0%";
  } else {
    return Math.round(score) + "%";
  }
}

export function formatDate(date, timezone, dateFormat = "dd MMM yyyy") {
  if (!date) return "--";
  const zonedDate = utcToZonedTime(date, timezone);
  return date ? format(zonedDate, dateFormat) : "--";
}

export function formatDateTime(date, timezone) {
  return formatDate(date, timezone, "dd MMM yyyy HH:mm");
}

export function convertToTimeZoneAndSkipSeconds(datetime, timeZone) {
  if (!datetime) return "--";
  const zonedDate = utcToZonedTime(datetime, timeZone);
  zonedDate.setSeconds(0);
  zonedDate.setMilliseconds(0);
  return zonedDate;
}

export function processLessonDetailedReportData(data, timezone, assignmentDetail) {
  const assignmentNormalized = [];
  const history = (data["history"] && data["history"]["lesson"]) || {};

  data["detail"].forEach((detail, i) => {
    const obj = assignmentDetail.find(item => parseInt(item.id) === parseInt(detail["id"]));
    const ele = {
      full_name: i > 0 ? "" : data["full_name"],
      email: i > 0 ? "" : data["email"],
      lesson_title: obj["name"],
      highest_score: detail["score"] ? formatScore(detail["score"]) : "--",
      status: detail["status"] || "Missed",
      completion_date_first_time: detail["first_date_complete"]
        ? formatDate(detail["first_date_complete"], timezone)
        : "--",
    };

    if ("highest_lesson_score" in history && detail["id"] in history["highest_lesson_score"]) {
      ele["highest_score"] = formatScore(history["highest_lesson_score"][detail["id"]]);
    }

    assignmentNormalized.push(ele);
  });

  assignmentNormalized.push({
    full_name: "",
    email: "",
    lesson_title: "Assignment score",
    highest_score: data["assignment_score"] > 0 ? formatScore(data["assignment_score"]) : "--",
    status: "--",
    completion_date_first_time: "--",
  });

  return assignmentNormalized;
}

/*export function processLessonDetailedReportDataOriginal(data, assignmentDetail) {
  const assignmentNormalized = [];

  data["detail"].forEach((detail, i) => {
    const lesson = assignmentDetail.find(lesson => lesson["id"] === detail["id"]);
    const lessonType = lessonTypes.find(type => type.value === lesson["game_type"]);
    const skill_type = lessonType ? lessonType["label"] : "";
    const level_of_difficulty = lesson["difficulty_level"] || "";
    const lesson_title = lesson["name"];

    const ele = {
      full_name: i > 0 ? "" : data["full_name"],
      email: i > 0 ? "" : data["email"],
      lesson_title,
      skill_type,
      level_of_difficulty,
      last_score: detail["score"] ? formatScore(detail["score"]) : "--",
    };

    assignmentNormalized.push(ele);
  });

  assignmentNormalized.push({
    full_name: "",
    email: "",
    lesson_title: "Assignment score",
    skill_type: "",
    level_of_difficulty: "",
    last_score: data["assignment_score"] > 0 ? formatScore(data["assignment_score"]) : "--",
  });

  return assignmentNormalized;
}*/

export function processLessonAssignmentsData(data, timezone, assignmentDetail) {
  const assignmentNormalized = [];
  const output = (data["history"]["lesson"] && data["history"]["lesson"]["output"]) || [];

  output.forEach((detail, i) => {
    let lesson_title, skill_type, level_of_difficulty;

    if (detail["lesson_title"]) {
      lesson_title = detail["lesson_title"];
    } else {
      const lesson = assignmentDetail.find(lesson => parseInt(lesson["id"]) === parseInt(detail["lesson_original_id"]));
      const lessonType = lessonTypes.find(type => type.value === lesson["game_type"]);
      skill_type = lessonType ? lessonType["label"] : "";
      level_of_difficulty = lesson["difficulty_level"] || "";
      lesson_title = lesson["name"];
    }

    const ele = {
      full_name: i > 0 ? "" : data["full_name"],
      email: i > 0 ? "" : data["email"],
      study_attempt: detail["study_attempt"],
      studied_time:
        detail["studied_time"] && detail["studied_time"] !== "--"
          ? formatDateTime(detail["studied_time"], timezone)
          : "--",
      lesson_title,
      skill_type,
      level_of_difficulty,
      score: detail["score"] ? formatScore(detail["score"]) : "--",
    };

    assignmentNormalized.push(ele);
  });

  return assignmentNormalized;
}

export function processSSDetailedReportData(data, timezone, assignmentDetail, largestSSTime, wsStudySetData) {
  let assignmentNormalized = [];
  let is4SkillSS = false;

  const calculateAverage = arr => {
    if (arr.length === 0) {
      return 0;
    }
    const sum = arr.reduce((total, value) => total + value.average_score, 0);
    return sum / arr.length;
  };

  const createEle = (i, obj) => {
    return {
      full_name: i > 0 ? "" : data["full_name"],
      email: i > 0 ? "" : data["email"],
      study_set: obj["name"],
      study_set_highest_score: data["detail"][i]["score"] ? formatScore(data["detail"][i]["score"]) : "--",
    };
  };

  const processScores = (ele, scores) => {
    for (let index = 0; index < scores.length; index++) {
      const score = scores[index];
      if (index > 0) {
        ele[`timestamp_${index}`] = formatDate(score["updated_at"], timezone);
        ele[`average_score_${index}`] = formatScore(score["average_score"]);
      } else {
        ele["status"] = score["status"];
        ele[`timestamp_${index}`] = formatDate(score["updated_at"], timezone);
        ele["number_of_attempts"] = scores.length;
        ele[`average_score`] = formatScore(calculateAverage(scores));
        ele[`average_score_${index}`] = formatScore(score["average_score"]);
      }
    }
  };

  const getPhraseIndex = (val, arr) => {
    const idx = arr.indexOf(val);
    return idx == -1 ? arr.length + 1 : idx + 1;
  };

  const generateSSItemData = (items, data, asDetail) => {
    let attempts = {};
    let is4Skill = false;

    const itemData = items.map(element => {
      let item_name = element["study_set_item"];
      const item_id = element["study_set_item_id"] || "average_score";

      attempts[item_id] = (attempts[item_id] || 0) + 1;

      if ("average_score" in element["scores"]) {
        item_name = "Overall Score";
      }

      let speaking = "";
      if (Object.keys(element["scores"]).length > 1) {
        is4Skill = true;
        speaking = element["scores"]["speaking"] ? formatScore(element["scores"]["speaking"]) : "";
      } else if (element["scores"].hasOwnProperty("speaking")) {
        speaking = formatScore(element["scores"]["speaking"]);
      }

      return {
        full_name: "",
        email: "",
        study_attempt: attempts[item_id],
        completion_date_time: formatDateTime(element["updated_at"]),
        item: item_name,
        score: formatScore(element["average_score"]),
        speaking: speaking,
        listening: is4Skill ? (element["scores"]["listening"] ? formatScore(element["scores"]["listening"]) : "") : "",
        reading: is4Skill ? (element["scores"]["reading"] ? formatScore(element["scores"]["reading"]) : "") : "",
        writing: is4Skill ? (element["scores"]["writing"] ? formatScore(element["scores"]["writing"]) : "") : "",
        ordering: getPhraseIndex(item_name, asDetail["phrases"]) + attempts[item_id] * 10,
      };
    });

    itemData.sort((a, b) => a.ordering - b.ordering);
    itemData.forEach(item => delete item.ordering);

    itemData[0]["full_name"] = data["full_name"];
    itemData[0]["email"] = data["email"];

    return { is4Skill, itemData };
  };

  const limitSheetName = (inputName, suffix) => {
    const maxLength = 31 - suffix.length;

    if (inputName.length > maxLength) {
      inputName = inputName.substring(0, maxLength - 3) + "...";
    }

    return inputName + suffix;
  };

  const processStudySetDetail = (ssId, ss) => {
    const output = (data["history"]["study_set"] && data["history"]["study_set"][ssId]["output"]) || [];
    const ssItemData = generateSSItemData(output, data, ss);
    const itemData = ssItemData["itemData"];
    is4SkillSS = ssItemData["is4Skill"];

    if (ssId in wsStudySetData) {
      wsStudySetData[ssId]["data"].push(...itemData);
    } else {
      wsStudySetData[ssId] = {
        name: limitSheetName(ss["name"] + " - Detailed Report", ""),
        data: itemData,
      };
    }
  };

  const len = data["detail"].length;

  for (let i = 0; i < len; i++) {
    const ssId = data["detail"][i]["id"];
    const ss = assignmentDetail.find(item => parseInt(item.id) === parseInt(ssId));
    let ele = createEle(i, ss);

    if (data["history"]["study_set"] && data["history"]["study_set"].hasOwnProperty(ssId)) {
      const scores = data["history"]["study_set"][ssId]["average_score"];
      if (scores.length > largestSSTime) {
        largestSSTime = scores.length;
      }
      processScores(ele, scores);
      processStudySetDetail(ssId, ss);
    } else {
      ele["status"] = "Missed";
      ele[`timestamp_0`] = "--";
      ele["number_of_attempts"] = "0";
      ele[`average_score`] = "0";
    }
    assignmentNormalized.push(ele);
  }

  assignmentNormalized.push({
    full_name: "",
    email: "",
    study_set: "Assignment Score",
    study_set_highest_score: data["assignment_score"] > 0 ? formatScore(data["assignment_score"]) : "--",
  });

  return { assignmentNormalized, largestSSTime, is4SkillSS };
}

export function processReadAloudDetailedReportData(data, timezone, assignmentDetail, detail) {
  const assignmentNormalized = [];

  detail.forEach((item, i) => {
    const obj = assignmentDetail.find(as => parseInt(as.id) === parseInt(item.id));

    let highestScore = item.score ? formatScore(item.score) : "--";
    if (data.history && data.history.read_aloud && item.id in data.history.read_aloud) {
      const output = data.history.read_aloud[item.id].output;
      let maxScore = output[0].eps_score;
      output.forEach(item => {
        if (item.eps_score > maxScore) {
          maxScore = item.eps_score;
        }
      });
      highestScore = formatScore(maxScore);
    }

    const ele = {
      full_name: i > 0 ? "" : data.full_name,
      email: i > 0 ? "" : data.email,
      lesson_title: obj.name,
      highest_score: highestScore,
      status: item.status || "Missed",
      completion_date_first_time: item.first_date_complete ? formatDate(item.first_date_complete, timezone) : "--",
    };

    assignmentNormalized.push(ele);
  });

  assignmentNormalized.push({
    full_name: "",
    email: "",
    lesson_title: "Assignment score",
    highest_score: data.assignment_score > 0 ? formatScore(data.assignment_score) : "--",
    status: "--",
    completion_date_first_time: "--",
  });

  return assignmentNormalized;
}

/*export function processReadAloudDetailedReportDataOriginal(data, assignmentDetail) {
  const assignmentNormalized = [];

  data["detail"].forEach((detail, i) => {
    const obj = assignmentDetail.find(item => item.id === detail["id"]);
    const ele = {
      full_name: i > 0 ? "" : data["full_name"],
      email: i > 0 ? "" : data["email"],
      lesson_title: obj["name"],
      skill_type: detail["assignment_type"] === "read_aloud" ? "Read Aloud" : "Warm-up",
      level_of_difficulty: "n/a",
      last_score: detail["score"] ? formatScore(detail["score"]) : "--",
    };

    assignmentNormalized.push(ele);
  });

  assignmentNormalized.push({
    full_name: "",
    email: "",
    lesson_title: "Assignment score",
    skill_type: "",
    level_of_difficulty: "",
    last_score: data["assignment_score"] > 0 ? formatScore(data["assignment_score"]) : "--",
  });

  return assignmentNormalized;
}*/

export function processReadAloudLessonAssignmentsData(data, timezone, assignmentDetail, detail) {
  const assignmentNormalized = [];
  const ra = assignmentDetail.find(as => as.assignment_type === "read_aloud");
  const raDetail = data.detail.find(as => as.assignment_type === "read_aloud");
  let output = [];

  if (ra !== undefined) {
    output = (data.history.read_aloud && data.history.read_aloud[ra["id"]].output) || [];
  }

  if (!raDetail.first_date_complete) {
    return assignmentNormalized;
  }

  let attempt = 0;
  let study_set_score = 0;

  output.forEach((history, idx) => {
    attempt++;

    let read_aloud_score;

    detail.forEach((item, i) => {
      const obj = assignmentDetail.find(as => parseInt(as.id) === parseInt(item.id));
      const is_read_aloud = item.assignment_type === "read_aloud";
      const ele = {
        full_name: idx === 0 && i === 0 ? data.full_name : "",
        email: idx === 0 && i === 0 ? data.email : "",
        study_attempt: attempt,
        studied_time:
          idx > 0 ? "--" : item.first_date_complete ? formatDateTime(item.first_date_complete, timezone) : "--",
        lesson_title: obj.name,
        skill_type: is_read_aloud ? "Read Aloud" : "Warm-up",
        level_of_difficulty: "n/a",
        score: item.score ? formatScore(item.score) : "--",
        eps_score: "",
        fluency_score: "",
        pronunciation_score: "",
        intonation_score: "",
      };

      if (is_read_aloud) {
        ele.studied_time = formatDateTime(history.timestamp, timezone);
        ele.eps_score = formatScore(history.eps_score);
        ele.pronunciation_score = formatScore(history.pronunciation_score);
        ele.intonation_score = formatScore(history.intonation_score);
        ele.fluency_score = formatScore(history.fluency_score);
        ele.score = formatScore(history.eps_score);

        read_aloud_score = history.eps_score;
      } else {
        study_set_score = item.score || 0;
      }

      assignmentNormalized.push(ele);
    });

    const averageScore = (study_set_score + read_aloud_score) / 2;

    assignmentNormalized.push({
      full_name: "",
      email: "",
      study_attempt: "",
      studied_time: "",
      lesson_title: "Assignment Score",
      skill_type: "",
      level_of_difficulty: "",
      score: !isNaN(averageScore) ? formatScore(averageScore) : "--",
      eps_score: "",
      fluency_score: "",
      pronunciation_score: "",
      intonation_score: "",
    });
  });

  return assignmentNormalized;
}

export function processAITutorDetailedReportData(data, timezone, assignmentDetail) {
  const assignmentNormalized = [];

  data["detail"].forEach(item => {
    const obj = assignmentDetail.find(as => as.assignment_detail_id === item.assignment_detail_id);

    let numberOfAttempts = 1;
    let average = null;
    if (data.history && data.history.ai_tutor && item.id in data.history.ai_tutor) {
      const output = data.history.ai_tutor[item.id].output;
      numberOfAttempts = output.length;
      const totalSum = output.reduce((sum, item) => sum + item.average_score, 0);
      average = totalSum / numberOfAttempts;
    }

    const ele = {
      full_name: data.full_name,
      email: data.email,
      lesson_title: obj.name,
      highest_score: item["score"] ? formatScore(item["score"]) : "--",
      status: item.status || "Missed",
      completion_date_first_time: item.first_date_complete ? formatDate(item.first_date_complete, timezone) : "--",
      number_of_attempts: numberOfAttempts,
      average_score: average ? formatScore(average) : "--",
    };

    assignmentNormalized.push(ele);
  });

  return assignmentNormalized;
}

export function processAITutorAssignmentsData(data, timezone, assignmentDetail) {
  const assignmentNormalized = [];

  data["detail"].forEach(item => {
    const obj = assignmentDetail.find(as => as.assignment_detail_id === item.assignment_detail_id);
    let attempt = 0;

    if (data.history && data.history.ai_tutor && item.id in data.history.ai_tutor) {
      const output = data.history.ai_tutor[item.id].output;

      output.forEach((history, idx) => {
        attempt++;

        const ele = {
          full_name: idx > 0 ? "" : data.full_name,
          email: idx > 0 ? "" : data.email,
          study_attempt: attempt,
          studied_time: history.timestamp ? formatDateTime(history.timestamp, timezone) : "--",
          lesson_title: obj.name,
          skill_type: "ELSA AI",
          level_of_difficulty: obj.difficulty_level,
          average_score: history.average_score ? formatScore(history.average_score) : "--",
          goals_completed: `${history.goals_completed}/${history.total_goals}`,
          pronunciation: history.pronunciation ? history.pronunciation.toUpperCase() : "",
          vocabulary: history.vocabulary ? history.vocabulary.toUpperCase() : "",
          grammar: history.grammar ? history.grammar.toUpperCase() : "",
          hints_used: history.hints_used,
          // user_turns: history.user_turns,
          duration: formatDuration(history.duration_in_secs),
        };

        assignmentNormalized.push(ele);
      });
    }
  });

  return assignmentNormalized;
}

function formatDuration(durationInSeconds) {
  const minutes = Math.floor(durationInSeconds / 60);
  const seconds = durationInSeconds % 60;

  // Zero-padding minutes and seconds
  const formattedMinutes = String(minutes).padStart(2, "0");
  const formattedSeconds = String(seconds).padStart(2, "0");

  return `${formattedMinutes}:${formattedSeconds}`;
}
